import { Injectable } from '@angular/core';

import { CmsUserQuickpay } from 'store/cms/models';
import { XmStore, XmStoreUtil } from 'services/store';
import { Modal } from 'services/modal';
import { Decorator } from 'services/decorator';

@Injectable()
export class SessionUtil {
    // 1 minute
    private static TIMEOUT_WARNING_COUNTDOWN: number = 60000;
    // 10 minutes
    private static TIMEOUT_INTERVAL: number = 60000 * 10;

    public sessionId: string;

    private inactivityTimeoutId: number;
    private warningTimeoutId: number;
    private homepageUrl: string;
    private timeoutWarningModalData: DescriptiveModalData;
    private timeoutConfirmationModalData: DescriptiveModalData;

    private decorator: Decorator;
    private modal: Modal;
    private xmStore: XmStore;

    constructor(decorator: Decorator, modal: Modal, xmStore: XmStore) {
        Object.assign(this, { decorator, modal, xmStore });

        XmStoreUtil.defaultCatch(this.xmStore.peekPromise<CmsUserQuickpay>(CmsUserQuickpay).then((result: CmsUserQuickpay) => {
            this.timeoutConfirmationModalData = result.timeoutConfirmationModal;
            this.timeoutWarningModalData = result.timeoutWarningModal;
            this.homepageUrl = result.homepageUrl;
        }));
    }

    public initSession(isUser: boolean = false): string {
        if (isUser) {
            this.setupInactivityTimers();
        }
        if (this.sessionId) {
            return this.sessionId;
        }

        const now: Date = new Date();
        this.sessionId = (now.getUTCMilliseconds()).toString();

        return this.sessionId;
    }

    public resetSession(): void {
        this.sessionId = '';
    }

    public clearInactivityTimer(): void {
        document.removeEventListener('mousemove', this.resetInactivityTimer.bind(this), false);
        document.removeEventListener('mousedown', this.resetInactivityTimer.bind(this), false);
        document.removeEventListener('keypress', this.resetInactivityTimer.bind(this), false);
        document.removeEventListener('touchmove', this.resetInactivityTimer.bind(this), false);

        if (!this.inactivityTimeoutId) {
            return;
        }

        window.clearTimeout(this.inactivityTimeoutId);
    }

    public resetInactivityTimer(): void {
        window.clearTimeout(this.inactivityTimeoutId);
        this.startInactivityTimer();
    }

    private openInactivityModals(): void {
        this.clearInactivityTimer();
        this.startWarningTimer();
        this.modal.descriptive(this.timeoutWarningModalData).toPromise()
            .then((response: ModalResponse) => {
                if (response && response.primary) {
                    this.clearWarningTimer();
                    this.setupInactivityTimers();
                }

                if (response && response.secondary) {
                    this.resetSession();
                    this.clearInactivityTimer();

                    window.location.href = this.decorator.createCrossAppUrl(this.homepageUrl);
                }
            });
    }

    private clearWarningTimer(): void {
        window.clearTimeout(this.warningTimeoutId);
    }

    private setupInactivityTimers(): void {
        document.addEventListener('mousemove', this.resetInactivityTimer.bind(this), false);
        document.addEventListener('mousedown', this.resetInactivityTimer.bind(this), false);
        document.addEventListener('keypress', this.resetInactivityTimer.bind(this), false);
        document.addEventListener('touchmove', this.resetInactivityTimer.bind(this), false);

        this.startInactivityTimer();
    }

    private startInactivityTimer(): void {
        // window.setTimeout returns an Id that can be used to start and stop a timer
        this.inactivityTimeoutId = window.setTimeout(() => {
            this.openInactivityModals();
        }, SessionUtil.TIMEOUT_INTERVAL);
    }

    private startWarningTimer(): void {
        this.warningTimeoutId = window.setTimeout(() => {
            this.openTimeoutConfirmationModal();
        }, SessionUtil.TIMEOUT_WARNING_COUNTDOWN);
    }

    private openTimeoutConfirmationModal(): void {
        this.clearWarningTimer();
        this.resetSession();
        this.modal.closeAll();
        this.modal.descriptive(this.timeoutConfirmationModalData).toPromise()
            .then(() => {
                window.location.href = this.decorator.createCrossAppUrl(this.homepageUrl);
            });
    }
}
